import React, { useState } from "react";
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  CircularProgress, 
  Paper,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  Container,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useClerk, useSignIn } from "@clerk/clerk-react";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { Visibility, VisibilityOff, LockOutlined, ArrowBack } from "@mui/icons-material";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/dashboard';
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Password recovery states
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Clerk hooks
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const clerk = useClerk();

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Validate login form
  const validateLoginForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
      isValid = false;
    }

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Validate password recovery form
  const validateRecoveryForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
      isValid = false;
    }

    if (codeSent) {
      if (!recoveryCode.trim()) {
        errors.recoveryCode = 'Verification code is required';
        isValid = false;
      }

      if (!newPassword) {
        errors.newPassword = 'New password is required';
        isValid = false;
      } else if (newPassword.length < 8) {
        errors.newPassword = 'Password must be at least 8 characters long';
        isValid = false;
      }

      if (!confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
        isValid = false;
      } else if (newPassword !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle user login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateLoginForm()) {
      return;
    }

    if (!signIn) {
      setError('System error: Unable to initialize the login process.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });
      
      if (result.status === "complete") {
        console.log("Login successful, setting active session");
        
        try {
          await clerk.setActive({ session: result.createdSessionId });
          console.log("Session activated successfully, ID:", result.createdSessionId);
          navigate(redirectPath);
        } catch (sessionError) {
          console.error("Error activating session:", sessionError);
          setError("Error establishing session. Please try again.");
          setLoading(false);
        }
      } else {
        console.log("Incomplete login status:", result.status);
        setError('Unable to sign in with those credentials.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Error signing in. Please check your credentials and try again.');
      setLoading(false);
    }
  };

  // Handle password recovery
  const handlePasswordRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateRecoveryForm()) {
      return;
    }

    if (!signIn) {
      setError('System error: Unable to initialize the password recovery process.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!codeSent) {
        // Step 1: Request reset code
        const result = await signIn.create({
          strategy: "reset_password_email_code",
          identifier: email,
        });

        if (result.status === "needs_first_factor") {
          setCodeSent(true);
          setError(null);
        } else {
          setError('Unable to send reset code. Please try again.');
        }
      } else {
        // Step 2: Reset password with code
        const result = await signIn.attemptFirstFactor({
          strategy: "reset_password_email_code",
          code: recoveryCode,
          password: newPassword,
        });

        if (result.status === "complete") {
          try {
            await clerk.setActive({ session: result.createdSessionId });
            navigate(redirectPath);
          } catch (sessionError) {
            console.error("Error activating session:", sessionError);
            setError("Error establishing session. Please try again.");
          }
        } else {
          setError('Invalid verification code. Please try again.');
        }
      }
    } catch (error) {
      console.error('Password recovery error:', error);
      setError('Error processing your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to sign up page
  const handleSignUp = () => {
    navigate(`/signup${redirectPath !== '/dashboard' ? `?redirect=${redirectPath}` : ''}`);
  };

  // Reset password recovery state
  const handleBackToLogin = () => {
    setIsRecoveringPassword(false);
    setCodeSent(false);
    setRecoveryCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setFormErrors({});
  };

  if (!isSignInLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f8"
      }}
    >
      <Header />

      <Container maxWidth="sm" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {/* Welcome Card */}
        <Card 
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #5D5FEF 0%, #4A4BD5 100%)',
            color: 'white',
            boxShadow: '0px 8px 20px rgba(93, 95, 239, 0.25)',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <LockOutlined sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h4" fontWeight="bold">
                {isRecoveringPassword ? 'Reset Password' : 'Welcome Back!'}
              </Typography>
            </Box>
            <Typography variant="body1">
              {isRecoveringPassword 
                ? 'Enter your email to receive a verification code and reset your password.'
                : 'Sign in to your Tranzcribe account to access your transcriptions, summaries and more.'}
            </Typography>
          </CardContent>
        </Card>

        {isRecoveringPassword && (
          <Stepper activeStep={codeSent ? 1 : 0} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Request Code</StepLabel>
            </Step>
            <Step>
              <StepLabel>Reset Password</StepLabel>
            </Step>
          </Stepper>
        )}

        <Paper 
          elevation={3} 
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}
        >
          {isRecoveringPassword && (
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToLogin}
              sx={{ mb: 2 }}
            >
              Back to Login
            </Button>
          )}

          <Box component="form" onSubmit={isRecoveringPassword ? handlePasswordRecovery : handleLogin}>
            <Typography variant="h5" gutterBottom fontWeight="600">
              {isRecoveringPassword ? 'Reset Password' : 'Sign In'}
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              
              {!isRecoveringPassword && (
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {isRecoveringPassword && codeSent && (
                <>
                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    error={!!formErrors.recoveryCode}
                    helperText={formErrors.recoveryCode}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    error={!!formErrors.newPassword}
                    helperText={formErrors.newPassword}
                    variant="outlined"
                    sx={{ mb: 2 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleNewPassword}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!formErrors.confirmPassword}
                    helperText={formErrors.confirmPassword}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: "#5D5FEF",
                color: "#fff",
                py: 1.5,
                mb: 2,
                '&:hover': {
                  backgroundColor: "#4A4BD5",
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 
                isRecoveringPassword 
                  ? (codeSent ? 'Reset Password' : 'Send Reset Code')
                  : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              {!isRecoveringPassword ? (
                <>
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={() => setIsRecoveringPassword(true)}
                    sx={{ color: '#5D5FEF', textDecoration: 'none', fontWeight: 500, mb: 2, display: 'block' }}
                  >
                    Forgot your password?
                  </Link>
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={handleSignUp}
                    sx={{ color: '#5D5FEF', textDecoration: 'none', fontWeight: 500 }}
                  >
                    Don't have an account? Sign Up
                  </Link>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {codeSent 
                    ? 'Enter the verification code sent to your email'
                    : 'We will send a verification code to your email'}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default Login;
